import { useCallback, useEffect, useState } from 'react';
import { readCache, writeCache, pruneCacheGroup } from '../../../services/localCache';
import { createPortal } from 'react-dom';
import { Users, ChevronDown, ChevronRight, AlertTriangle, Settings2, X, Clock, CheckCircle2, RotateCcw, ClipboardX, UserCheck, Percent, Gift, Plus, Pencil, SlidersHorizontal } from 'lucide-react';
import {
  getPayroll, getTeacherRates, setTeacherRate, payPayroll, undoPayroll, setPayrollException, deletePayrollException,
  getCommission, deliverCommission, undoCommissionDelivery, addPayrollExtra, deletePayrollExtra, EXTRA_CONCEPTS, closePayrollWeek, reopenPayrollWeek,
  PayrollResp, PayrollTeacher, PayrollSession, TeacherRate, CommissionResp, ExtraConcept, moneyEntero as money,
  getAdjustmentConcepts, addPayrollAdjustment, deletePayrollAdjustment, AdjustmentConcept, PayrollAdjustment,
  setPayrollDirectAmount, MONTO_DIRECTO,
} from '../api';
import { teacherPhotoUrl } from '../../teachers/api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import { useToast } from '../../../context/ToastContext';
import { todayLocal, addMonthsClamped } from '../../../utils/dates';

type Range = 'day' | 'week' | 'month';

function fromISO(d: string) { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); }
function toISO(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function step(anchor: string, range: Range, delta: number) {
  if (range === 'month') return addMonthsClamped(anchor, delta); // sin saltos en días 29-31
  const d = fromISO(anchor);
  d.setDate(d.getDate() + (range === 'day' ? delta : delta * 7));
  return toISO(d);
}
function periodLabel(r: PayrollResp | null, range: Range) {
  if (!r) return '—';
  const f = fromISO(r.from);
  if (range === 'day') return f.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  if (range === 'month') return f.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  // Semana de LUNES A SÁBADO (Karla, 15-sep-2026: «la semana es del 7 al 12»). El estudio no da clase
  // en domingo; el rango interno sigue siendo lun-dom para no perder nada, solo cambia la etiqueta.
  const last = fromISO(r.from); last.setDate(last.getDate() + 5);
  return `${f.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} – ${last.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`;
}
function fmtDay(d: string) { const x = fromISO(d); return x.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' }); }
function monthLabel(m: string) { return fromISO(`${m}-01`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }); }
const CONCEPT: Record<string, string> = { mensualidad: 'Mensualidad', curso: 'Curso completo', inscripcion: 'Inscripción', multa: 'Pago tardío', clase_suelta: 'Clase suelta' };
const METHOD: Record<string, string> = { cash: 'Efectivo', wire: 'Transferencia', creditcard: 'Tarjeta', card: 'Tarjeta' };

/** Modal para fijar cómo cobra cada persona: maestros por hora (con tarifa de mañana opcional) o por
 *  comisión, y sueldo FIJO semanal para el staff (Mabel 3-oct-2026: «Araceli $2,000, Dayan $1,500»). */
function RatesModal({ onClose, onSaved, focusId }: { onClose: () => void; onSaved: () => void; focusId?: number }) {
  const toast = useToast();
  const [rows, setRows] = useState<TeacherRate[] | null>(null);
  type V = { mode: 'hourly' | 'commission'; rate: string; morning: string; until: string; pct: string; salary: string };
  const [vals, setVals] = useState<Record<number, V>>({});
  const [saving, setSaving] = useState(false);
  const toV = (t: TeacherRate): V => ({
    mode: t.pay_mode || 'hourly', rate: String(Number(t.hourly_rate) || ''),
    morning: t.morning_rate != null ? String(Number(t.morning_rate)) : '', until: t.morning_until || '12:00',
    pct: t.commission_pct != null ? String(Number(t.commission_pct)) : '',
    salary: t.salary_weekly != null && Number(t.salary_weekly) > 0 ? String(Number(t.salary_weekly)) : '',
  });

  useEffect(() => {
    getTeacherRates().then((r) => { setRows(r.data); setVals(Object.fromEntries(r.data.map((t) => [t.account_id, toV(t)]))); });
  }, []);
  const set = (id: number, patch: Partial<V>) => setVals((v) => ({ ...v, [id]: { ...v[id], ...patch } }));
  // ✏️ del renglón (Mabel 5-oct: «¿dónde edito el sueldo?»): abre aquí con el cursor en el sueldo de esa persona.
  useEffect(() => {
    if (!rows || !focusId) return;
    const i = document.querySelector<HTMLInputElement>(`[data-testid="salary-${focusId}"]`);
    i?.scrollIntoView({ block: 'center' }); i?.focus(); i?.select();
  }, [rows, focusId]);

  async function save() {
    if (!rows) return;
    setSaving(true);
    try {
      for (const t of rows) {
        const v = vals[t.account_id]; const o = toV(t);
        if (JSON.stringify(v) === JSON.stringify(o)) continue;
        await setTeacherRate(t.account_id, Number(v.rate || 0), {
          pay_mode: v.mode, morning_rate: v.morning === '' ? null : Number(v.morning), morning_until: v.until || '12:00',
          commission_pct: v.mode === 'commission' ? Number(v.pct || 0) : null,
          salary_weekly: v.salary === '' ? null : Number(v.salary),
        });
      }
      toast.success('Tarifas guardadas');
      onSaved(); onClose();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }

  const inp = 'rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-right text-sm text-content outline-none focus:border-brand-blue';
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Cómo cobra cada persona</h3>
          <button onClick={onClose} className="text-content-3 hover:text-content"><X size={18} /></button>
        </div>
        <p className="mb-4 text-xs text-content-3">
          <b>Por hora</b>: tarifa × horas programadas (opcional: otra tarifa para las clases de la mañana).
          <b> Comisión</b>: % de lo que pagan las alumnas de sus clases (mensualidad, inscripción, pagos tardíos, clase suelta).
          <b> Sueldo fijo</b>: cantidad semanal por persona; se suma a sus clases si también da.
        </p>
        {!rows ? <Skeleton className="h-40 w-full" /> : (
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-content-3">Maestros</div>
            {rows.filter((t) => t.es_profesor).map((t) => { const v = vals[t.account_id]; if (!v) return null; return (
              <div key={t.account_id} className="rounded-xl border border-line p-3">
                <div className="flex items-center gap-3">
                  {t.has_photo
                    ? <img src={teacherPhotoUrl(t.account_id)} alt="" className="h-8 w-8 rounded-full object-cover" />
                    : <div className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-xs font-medium text-content-3">{t.name?.[0]}</div>}
                  <span className="flex-1 truncate text-sm font-medium text-content">{t.name}</span>
                  <div className="inline-flex rounded-lg border border-line bg-surface p-0.5">
                    {([['hourly', 'Por hora'], ['commission', 'Comisión']] as const).map(([k, label]) => (
                      <button key={k} onClick={() => set(t.account_id, { mode: k })}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${v.mode === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>{label}</button>
                    ))}
                  </div>
                </div>
                {v.mode === 'hourly' ? (
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 pl-11 text-xs text-content-3">
                    <label className="flex items-center gap-1">$<input type="number" min="0" step="10" value={v.rate} onChange={(e) => set(t.account_id, { rate: e.target.value })} className={`w-20 ${inp}`} />/h</label>
                    <label className="flex items-center gap-1" title="Solo las clases que empiezan antes de la hora indicada">mañana $<input type="number" min="0" step="10" placeholder="igual" value={v.morning} onChange={(e) => set(t.account_id, { morning: e.target.value })} className={`w-20 ${inp}`} />/h</label>
                    {v.morning !== '' && <label className="flex items-center gap-1">hasta <input type="time" value={v.until} onChange={(e) => set(t.account_id, { until: e.target.value })} className={`${inp} text-left`} /></label>}
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-1 pl-11 text-xs text-content-3">
                    <input type="number" min="1" max="100" step="1" value={v.pct} onChange={(e) => set(t.account_id, { pct: e.target.value })} className={`w-16 ${inp}`} />% de lo que pagan sus alumnas
                  </div>
                )}
                <div className="mt-2 flex items-center gap-1 pl-11 text-xs text-content-3" title="Solo si además tiene un sueldo fijo (p. ej. administrativa que da clases)">
                  sueldo fijo $<input type="number" min="0" step="100" placeholder="no" value={v.salary} data-testid={`salary-${t.account_id}`} onChange={(e) => set(t.account_id, { salary: e.target.value })} className={`w-24 ${inp}`} />/semana
                </div>
              </div>
            ); })}
            <div className="pt-2 text-xs font-semibold uppercase tracking-wide text-content-3">Staff</div>
            {rows.filter((t) => !t.es_profesor).length === 0 && <p className="text-xs text-content-3">Nadie del equipo con acceso.</p>}
            {rows.filter((t) => !t.es_profesor).map((t) => { const v = vals[t.account_id]; if (!v) return null; return (
              <div key={t.account_id} className="rounded-xl border border-line p-3" data-testid={`staff-rate-${t.account_id}`}>
                <div className="flex items-center gap-3">
                  {t.has_photo
                    ? <img src={teacherPhotoUrl(t.account_id)} alt="" className="h-8 w-8 rounded-full object-cover" />
                    : <div className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-xs font-medium text-content-3">{t.name?.[0]}</div>}
                  <span className="flex-1 truncate text-sm font-medium text-content">{t.name}</span>
                  <label className="flex items-center gap-1 text-xs text-content-3">sueldo fijo $<input type="number" min="0" step="100" placeholder="no" value={v.salary} data-testid={`salary-${t.account_id}`} onChange={(e) => set(t.account_id, { salary: e.target.value })} className={`w-24 ${inp}`} />/semana</label>
                </div>
              </div>
            ); })}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
          <button onClick={save} disabled={saving} className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Línea manual de nómina (Mabel 3-oct-2026): bono de puntualidad / desempeño, comisión, sueldo extra u otro, por
 *  persona, a mano. Se registra como gasto al guardarla (cada concepto con su nombre en Gastos). */
function ExtraModal({ person, people, range, anchor, periodText, onClose, onSaved }: {
  person: { id: number; name: string } | null; people: TeacherRate[]; range: Range; anchor: string; periodText: string;
  onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [userId, setUserId] = useState<number>(person?.id || 0);
  const [concept, setConcept] = useState<ExtraConcept>('bono_puntualidad');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const inp = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue';
  async function save() {
    if (!userId) { toast.error('Elige a la persona'); return; }
    if (!(Number(amount) > 0)) { toast.error('Escribe el monto'); return; }
    if (concept === 'otro' && !label.trim()) { toast.error('Escribe el concepto'); return; }
    setSaving(true);
    try {
      const body = { user_id: userId, range, date: anchor, concept, label: label.trim() || undefined, amount: Number(amount), note: note.trim() || undefined };
      try { await addPayrollExtra(body); }
      catch (e: any) {
        // Fuera de la semana abierta (Mabel 9-oct-2026): el servidor pide confirmar
        if (e?.status === 409 && e?.data?.need_confirm) { if (!confirm(e.message)) return; await addPayrollExtra({ ...body, confirm: true }); }
        else throw e;
      }
      toast.success('Línea registrada como gasto de nómina');
      onSaved(); onClose();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }
  const profes = people.filter((p) => p.es_profesor), staff = people.filter((p) => !p.es_profesor);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl" data-testid="extra-modal">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Bono, comisión u otro</h3>
          <button onClick={onClose} className="text-content-3 hover:text-content"><X size={18} /></button>
        </div>
        <p className="mb-4 text-xs text-content-3">Se registra en Nómina con su concepto para el periodo <span className="capitalize">{periodText}</span>. Queda en Gastos como «Nómina / concepto».</p>
        <div className="space-y-3">
          <label className="block text-xs text-content-3">Persona
            {person ? <div className="mt-1 rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm font-medium text-content">{person.name}</div> : (
              <select value={userId} onChange={(e) => setUserId(Number(e.target.value))} className={`mt-1 ${inp}`} data-testid="extra-person">
                <option value={0}>Elige…</option>
                {staff.length > 0 && <optgroup label="Staff">{staff.map((p) => <option key={p.account_id} value={p.account_id}>{p.name}</option>)}</optgroup>}
                {profes.length > 0 && <optgroup label="Maestros">{profes.map((p) => <option key={p.account_id} value={p.account_id}>{p.name}</option>)}</optgroup>}
              </select>
            )}
          </label>
          <div>
            <div className="text-xs text-content-3">Concepto</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(Object.keys(EXTRA_CONCEPTS) as ExtraConcept[]).map((k) => (
                <button key={k} type="button" onClick={() => setConcept(k)} data-testid={`extra-concept-${k}`}
                  className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${concept === k ? 'border-accent bg-accent text-on-accent' : 'border-line bg-surface text-content-2 hover:text-content'}`}>{EXTRA_CONCEPTS[k]}</button>
              ))}
            </div>
          </div>
          {(concept === 'otro' || concept === 'comision') && (
            <label className="block text-xs text-content-3">{concept === 'otro' ? 'Concepto (obligatorio)' : 'De qué es la comisión (opcional)'}
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} placeholder={concept === 'otro' ? 'p. ej. Apoyo transporte' : 'p. ej. Inscripciones de septiembre'} className={`mt-1 ${inp}`} data-testid="extra-label" />
            </label>
          )}
          <label className="block text-xs text-content-3">Monto
            <input type="number" min="1" step="50" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={`mt-1 ${inp} text-right`} data-testid="extra-amount" />
          </label>
          <label className="block text-xs text-content-3">Nota (opcional)
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} className={`mt-1 ${inp}`} data-testid="extra-note" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="extra-save" className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">{saving ? 'Guardando…' : 'Registrar'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Ajuste de la SEMANA (B4, Mabel 14/15-oct-2026: «poder modificar el sueldo si hay alguna situación, como falta, permisos,
 *  hrs extras»). Concepto + monto + nota, por persona, ANTES de «Registrar pago»: el pago registrado = calculado + ajustes.
 *  Ya no hace falta cambiar la tarifa y regresarla (la Conciliación lo marcaba como diferencia). Si la semana ya está
 *  pagada, solo se puede anotar como «ya incluido» (explica un pago distinto del calculado; no toca gastos). */
function AjusteModal({ t, anchor, periodText, onClose, onSaved }: {
  t: PayrollTeacher; anchor: string; periodText: string; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [conceptos, setConceptos] = useState<AdjustmentConcept[] | null>(null);
  const [concept, setConcept] = useState('falta');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { getAdjustmentConcepts().then((r) => setConceptos(r.data)).catch(() => setConceptos([])); }, []);
  const c = conceptos?.find((x) => x.key === concept);
  const signo = c?.signo ?? -1;
  const n = Number(amount) || 0;
  const neto = t.adjustments_total || 0;
  const despues = Math.round(((t.pay || 0) + neto + signo * n) * 100) / 100;
  const inp = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue';
  async function save() {
    if (!(n > 0)) { toast.error('Escribe el monto'); return; }
    if (c?.pide_texto && !label.trim()) { toast.error('Escribe el concepto'); return; }
    const body = { user_id: t.teacher_id, date: anchor, concept, amount: n, label: label.trim() || undefined, note: note.trim() || undefined, included: !!t.paid };
    setSaving(true);
    try {
      try { await addPayrollAdjustment(body); }
      catch (e: any) {
        // Fuera de la semana abierta (9-oct-2026): el servidor pide confirmar
        if (e?.status === 409 && e?.data?.need_confirm) { if (!confirm(e.message)) return; await addPayrollAdjustment({ ...body, confirm: true }); }
        else throw e;
      }
      toast.success(t.paid ? 'Ajuste anotado como «ya incluido» en el pago' : 'Ajuste guardado · se suma al registrar el pago');
      onSaved(); onClose();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }
  const grupo = (s: 1 | -1, titulo: string) => (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-content-3">{titulo}</div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {(conceptos || []).filter((x) => x.signo === s).map((x) => (
          <button key={x.key} type="button" onClick={() => setConcept(x.key)} data-testid={`ajuste-concepto-${x.key}`}
            className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${concept === x.key ? 'border-accent bg-accent text-on-accent' : 'border-line bg-surface text-content-2 hover:text-content'}`}>{x.label}</button>
        ))}
      </div>
    </div>
  );
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl" data-testid="ajuste-modal">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Ajuste de la semana</h3>
          <button onClick={onClose} className="text-content-3 hover:text-content"><X size={18} /></button>
        </div>
        <p className="mb-4 text-xs text-content-3">
          <b className="text-content">{t.teacher_name}</b> · semana <span className="capitalize">{periodText}</span>.
          {t.paid
            ? <> Esta semana <b>ya está pagada</b>: el ajuste se anota como <b>«ya incluido»</b> (explica por qué el pago fue distinto del calculado; no cambia ningún gasto). Para cambiar el pago, deshaz «Pagado», captura el ajuste y vuelve a registrarlo.</>
            : <> Se suma al <b>registrar el pago</b>; la tarifa no se toca. Los bonos quedan en Gastos con su concepto (Nómina › Bono…).</>}
        </p>
        {!conceptos ? <Skeleton className="h-24 w-full" /> : (
          <div className="space-y-3">
            {grupo(-1, 'Descuenta')}
            {grupo(1, 'Suma')}
            <label className="block text-xs text-content-3">{c?.pide_texto ? 'Concepto (obligatorio)' : 'Concepto propio (opcional, p. ej. «3 h sábado»)'}
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} className={`mt-1 ${inp}`} data-testid="ajuste-label" />
            </label>
            <label className="block text-xs text-content-3">Monto ({signo < 0 ? 'se descuenta' : 'se suma'})
              <input type="number" min="1" step="50" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={`mt-1 ${inp} text-right`} data-testid="ajuste-monto" />
            </label>
            <label className="block text-xs text-content-3">Nota (opcional)
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} placeholder="p. ej. faltó el miércoles" className={`mt-1 ${inp}`} data-testid="ajuste-nota" />
            </label>
            {!t.paid && (
              <div className="rounded-lg border border-line bg-surface-2/60 px-3 py-2 text-xs text-content-2" data-testid="ajuste-resumen">
                Calculado {money(t.pay || 0)}{neto ? ` · ajustes ya capturados ${neto > 0 ? '+' : '−'}${money(Math.abs(neto))}` : ''}{n > 0 ? ` · este ${signo < 0 ? '−' : '+'}${money(n)}` : ''} → <b className={despues < 0 ? 'text-red-600' : 'text-content'}>a pagar {money(despues)}</b>
              </div>
            )}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
          <button onClick={save} disabled={saving || !conceptos} data-testid="ajuste-guardar" className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">{saving ? 'Guardando…' : t.paid ? 'Anotar «ya incluido»' : 'Guardar ajuste'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** «Monto editado» de la persona en la semana (Mabel 19-oct-2026: «con respecto a la nómina poner la opción de poder editar
 *  directamente el monto»). Guarda UN ajuste `monto_directo` = nuevo − calculado (horario × tarifa + sueldo fijo + los demás
 *  ajustes); si ya hay uno se actualiza. Solo semanas SIN pagar (el lápiz no sale en las pagadas). La tarifa no se toca. */
function MontoModal({ t, anchor, periodText, onClose, onSaved }: {
  t: PayrollTeacher; anchor: string; periodText: string; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const directo = (t.adjustments || []).find((a) => a.concept === MONTO_DIRECTO) || null;
  const actual = t.pay_adjusted ?? t.pay ?? 0;
  const calculado = Math.round((actual - (directo ? directo.amount : 0)) * 100) / 100;
  const [amount, setAmount] = useState(String(actual));
  const [note, setNote] = useState(directo?.note || '');
  const [saving, setSaving] = useState(false);
  const n = amount.trim() === '' ? NaN : Number(amount);
  const dif = Number.isFinite(n) ? Math.round((n - calculado) * 100) / 100 : 0;
  const inp = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue';
  async function enviar(body: { amount?: number; note?: string; reset?: boolean }, okMsg: string) {
    const b = { user_id: t.teacher_id, date: anchor, ...body };
    setSaving(true);
    try {
      try { await setPayrollDirectAmount(b); }
      catch (e: any) {
        // Fuera de la semana abierta (9-oct-2026): el servidor pide confirmar
        if (e?.status === 409 && e?.data?.need_confirm) { if (!confirm(e.message)) return; await setPayrollDirectAmount({ ...b, confirm: true }); }
        else throw e;
      }
      toast.success(okMsg); onSaved(); onClose();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }
  function guardar() {
    if (!Number.isFinite(n)) { toast.error('Escribe el monto a pagar'); return; }
    if (n < 0) { toast.error('El monto no puede quedar en negativo'); return; }
    enviar({ amount: n, note: note.trim() || undefined }, dif === 0 ? 'Queda el monto calculado' : `A pagar a ${t.teacher_name}: ${money(n)}`);
  }
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl" data-testid="monto-modal">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Editar monto a pagar</h3>
          <button onClick={onClose} className="text-content-3 hover:text-content"><X size={18} /></button>
        </div>
        <p className="mb-4 text-xs text-content-3">
          <b className="text-content">{t.teacher_name}</b> · semana <span className="capitalize">{periodText}</span>. Cambia solo lo que se le paga esta
          semana; la tarifa no se toca. Se usa al <b>registrar el pago</b>.
        </p>
        <div className="space-y-3">
          <div className="rounded-lg border border-line bg-surface-2/60 px-3 py-2 text-xs text-content-2">
            Calculado <b className="text-content" data-testid="monto-calculado">{money(calculado)}</b>
            {(t.adjustments_total || 0) - (directo ? directo.amount : 0) !== 0 && <span className="text-content-3"> (horario + ajustes de la semana)</span>}
          </div>
          <label className="block text-xs text-content-3">Monto a pagar esta semana
            <input type="number" min="0" step="50" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus
              className={`mt-1 ${inp} text-right font-display text-lg font-bold`} data-testid="monto-input" />
          </label>
          {Number.isFinite(n) && (
            <div className={`text-xs ${n < 0 ? 'text-red-600' : 'text-content-2'}`} data-testid="monto-dif">
              {n < 0 ? 'No puede quedar en negativo.' : dif === 0 ? 'Igual a lo calculado.' : <>{dif > 0 ? 'Suma' : 'Descuenta'} <b>{money(Math.abs(dif))}</b> sobre lo calculado.</>}
            </div>
          )}
          <label className="block text-xs text-content-3">Nota (opcional)
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} placeholder="p. ej. faltó el miércoles" className={`mt-1 ${inp}`} data-testid="monto-nota" />
          </label>
          {directo && (
            <p className="text-[11px] text-content-3">Editado por {directo.created_by_name || '—'}{directo.created_at ? ` el ${directo.created_at}` : ''}.</p>
          )}
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          {directo && (
            <button onClick={() => enviar({ reset: true }, 'Volvió al monto calculado')} disabled={saving} data-testid="monto-reset"
              className="mr-auto inline-flex items-center gap-1 rounded-lg px-2 py-2 text-xs font-medium text-content-2 hover:bg-surface-2 hover:text-content"><RotateCcw size={13} /> Volver al calculado</button>
          )}
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
          <button onClick={guardar} disabled={saving || !Number.isFinite(n) || n < 0} data-testid="monto-guardar" className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Elegir suplente para una sesión. */
function SubstituteModal({ session, teachers, onClose, onSaved }: { session: PayrollSession; teachers: TeacherRate[]; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [who, setWho] = useState<number>(0);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!who) return;
    setSaving(true);
    try { await setPayrollException({ class_id: session.class_id, date: session.date, kind: 'substitute', substitute_user_id: who, note }); toast.success('Suplente registrado'); onSaved(); onClose(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-sm rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <h3 className="font-display text-lg font-bold text-content">¿Quién dio la clase?</h3>
        <p className="mb-3 text-xs text-content-3 capitalize">{fmtDay(session.date)} · {session.class_name}{session.start_time ? ` · ${session.start_time}` : ''}. La sesión se le paga al suplente con su tarifa.</p>
        <select value={who} onChange={(e) => setWho(Number(e.target.value))} className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue">
          <option value={0}>Elige al suplente…</option>
          {teachers.map((t) => <option key={t.account_id} value={t.account_id}>{t.name}</option>)}
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional)" className="mt-2 w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue" />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
          <button onClick={save} disabled={saving || !who} className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Hoja de comisión de un maestro para un mes: alumna por alumna, con «Entregado». */
function CommissionSheet({ teacherId, month, onChanged }: { teacherId: number; month: string; onChanged: () => void }) {
  const toast = useToast();
  const [data, setData] = useState<CommissionResp | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getCommission(teacherId, month).then((d) => { setData(d); setSel(new Set()); }), [teacherId, month]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-4"><Skeleton className="h-24 w-full" /></div>;
  const pendientes = data.items.filter((i) => !i.payout_id);
  const toggle = (id: number) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  async function entregar() {
    const ids = [...sel]; if (!ids.length) return;
    setBusy(true);
    try { await deliverCommission(teacherId, ids); toast.success(`Comisión de ${ids.length} pago(s) marcada como entregada`); await load(); onChanged(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo marcar'); }
    finally { setBusy(false); }
  }
  async function deshacer(id: number) {
    setBusy(true);
    try { await undoCommissionDelivery(id); await load(); onChanged(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo deshacer'); }
    finally { setBusy(false); }
  }
  return (
    <div className="border-t border-line bg-surface-2/40 px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-content-3">
        <span className="capitalize">{monthLabel(data.month)} · base {money(data.base)} · {data.pct}% = <b className="text-content">{money(data.commission)}</b> · entregado {money(data.delivered)} · <b className={data.pending > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-content'}>pendiente {money(data.pending)}</b></span>
        {pendientes.length > 0 && (
          <div className="flex items-center gap-2">
            <button onClick={() => setSel(new Set(pendientes.map((i) => i.payment_id)))} className="text-brand-blue hover:underline">Seleccionar pendientes</button>
            <button onClick={entregar} disabled={busy || sel.size === 0} className="rounded-lg bg-accent px-2.5 py-1.5 text-xs font-semibold text-on-accent disabled:opacity-60">
              Marcar entregado{sel.size ? ` (${money([...sel].reduce((a, id) => a + (data.items.find((i) => i.payment_id === id)?.commission || 0), 0))})` : ''}
            </button>
          </div>
        )}
      </div>
      {data.items.length === 0 ? <p className="py-3 text-center text-sm text-content-3">Sin pagos de sus alumnas este mes.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-content-3">
              <th className="py-1 pr-2"></th><th className="py-1 pr-2">Alumna</th><th className="py-1 pr-2">Clase</th><th className="py-1 pr-2">Concepto</th>
              <th className="py-1 pr-2 text-right">Pagó</th><th className="py-1 pr-2">Forma</th><th className="py-1 pr-2">Fecha</th><th className="py-1 pr-2 text-right">Comisión</th><th className="py-1">Entregado</th>
            </tr></thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.payment_id} className="border-t border-line/60">
                  <td className="py-1.5 pr-2">{!i.payout_id && <input type="checkbox" checked={sel.has(i.payment_id)} onChange={() => toggle(i.payment_id)} />}</td>
                  <td className="py-1.5 pr-2 text-content">{i.nombre}{!i.is_active && <span className="ml-1 text-[10px] text-content-3">(baja)</span>}</td>
                  <td className="py-1.5 pr-2 text-content-2">{i.clases}</td>
                  <td className="py-1.5 pr-2 text-content-2">{CONCEPT[i.concept] || i.concept}{i.concept === 'curso' && i.months ? <span className="block text-[10px] text-content-3">{i.months.split(',').length} meses</span> : null}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-content">{money(i.amount)}</td>
                  <td className="py-1.5 pr-2 text-content-3">{METHOD[i.method || ''] || i.method || '—'}</td>
                  <td className="py-1.5 pr-2 text-content-3">{fmtDay(i.pay_date)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums font-medium text-content">{i.commission != null ? money(i.commission) : '—'}</td>
                  <td className="py-1.5">
                    {i.payout_id
                      ? <button onClick={() => deshacer(i.payout_id!)} disabled={busy} title="Clic para deshacer" className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><CheckCircle2 size={13} /> {fmtDay(i.delivered_at!)}</button>
                      : <span className="text-xs text-amber-600 dark:text-amber-400">pendiente</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function PayrollPanel() {
  const toast = useToast();
  const [range, setRange] = useState<Range>('week');
  // '' = la semana abierta de nómina (la decide el servidor; Mabel 9-oct-2026). Al navegar se fija una fecha.
  const [anchor, setAnchor] = useState('');
  const [resp, setResp] = useState<PayrollResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showRates, setShowRates] = useState<boolean | number>(false);   // número = abrir enfocado en esa persona
  const [busy, setBusy] = useState<number | null>(null);
  const [teachers, setTeachers] = useState<TeacherRate[]>([]);
  const [subFor, setSubFor] = useState<PayrollSession | null>(null);
  const [extraFor, setExtraFor] = useState<{ id: number; name: string } | null | 'any'>(null);
  const [ajusteFor, setAjusteFor] = useState<PayrollTeacher | null>(null);
  const [montoFor, setMontoFor] = useState<PayrollTeacher | null>(null);

  const load = useCallback(() => {
    const key = `fin-payroll:${range}:${anchor}`;
    const cached = readCache<PayrollResp>(key);
    if (cached) { setResp(cached); setLoading(false); } else setLoading(true);
    getPayroll(range, anchor || undefined)
      .then((r) => { setResp(r); writeCache(key, r); pruneCacheGroup('fin-payroll', key); })
      .finally(() => setLoading(false));
  }, [range, anchor]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { getTeacherRates().then((r) => setTeachers(r.data)).catch(() => {}); }, [showRates]);

  const effDate = anchor || resp?.from || todayLocal();
  const nav = (delta: number) => setAnchor((a) => step(a || resp?.from || todayLocal(), range, delta));
  const pw = range === 'week' ? resp?.payroll_week ?? null : null;
  const [closing, setClosing] = useState(false);
  async function cerrarSemana() {
    if (!pw) return;
    const faltan = pw.pending;
    const msg = faltan.length
      ? `Faltan por pagar ${faltan.length}: ${faltan.map((p) => p.name).join(', ')}.\n\n¿Cerrar la semana de todos modos y pasar a la siguiente?`
      : '¿Cerrar esta semana y pasar a la siguiente?';
    if (!confirm(msg)) return;
    setClosing(true);
    try { await closePayrollWeek(faltan.length > 0); toast.success('Semana cerrada · ya estás en la siguiente'); setAnchor(''); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo cerrar la semana'); }
    finally { setClosing(false); }
  }
  async function reabrirSemana() {
    if (!confirm('¿Deshacer el último cierre y volver a la semana anterior?')) return;
    try { await reopenPayrollWeek(); toast.success('Volviste a la semana anterior'); setAnchor(''); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo deshacer'); }
  }

  async function pagar(t: PayrollTeacher) {
    setBusy(t.teacher_id);
    try {
      let r;
      try { r = await payPayroll(t.teacher_id, range, effDate); }
      catch (e: any) {
        // Fuera de la semana abierta (Mabel 9-oct-2026): el servidor pide confirmar
        if (e?.status === 409 && e?.data?.need_confirm) { if (!confirm(e.message)) return; r = await payPayroll(t.teacher_id, range, effDate, true); }
        else throw e;
      }
      toast.success(r?.adjustments ? `Sueldo de ${t.teacher_name} registrado con sus ajustes: ${money(r.total ?? r.amount)}` : `Sueldo de ${t.teacher_name} registrado como gasto`); load();
    }
    catch (e: any) { toast.error(e?.message || 'No se pudo registrar'); }
    finally { setBusy(null); }
  }
  async function deshacer(t: PayrollTeacher) {
    if (!t.run_id) return;
    if (!confirm(`¿Deshacer el registro de nómina de ${t.teacher_name}? Se borrará el gasto creado.`)) return;
    setBusy(t.teacher_id);
    try { await undoPayroll(t.run_id); toast.success('Registro deshecho'); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo deshacer'); }
    finally { setBusy(null); }
  }
  async function noSeDio(s: PayrollSession) {
    try { await setPayrollException({ class_id: s.class_id, date: s.date, kind: 'not_given' }); toast.success('Sesión marcada como no dada'); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo marcar'); }
  }
  async function quitarExtra(t: PayrollTeacher, id: number, label: string) {
    if (!confirm(`¿Quitar «${label}» de ${t.teacher_name}? Se borrará el gasto que creó.`)) return;
    try { await deletePayrollExtra(id); toast.success('Línea quitada'); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
  }
  async function quitarAjuste(t: PayrollTeacher, a: PayrollAdjustment) {
    if (!confirm(`¿Quitar el ajuste «${a.nombre}» (${a.amount < 0 ? '−' : '+'}${money(Math.abs(a.amount))}) de ${t.teacher_name}?`)) return;
    try { await deletePayrollAdjustment(a.id); toast.success('Ajuste quitado'); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
  }
  async function volverAlCalculado(t: PayrollTeacher) {
    if (!confirm(`¿Volver al monto calculado de ${t.teacher_name}?`)) return;
    try {
      try { await setPayrollDirectAmount({ user_id: t.teacher_id, date: effDate, reset: true }); }
      catch (e: any) {
        if (e?.status === 409 && e?.data?.need_confirm) { if (!confirm(e.message)) return; await setPayrollDirectAmount({ user_id: t.teacher_id, date: effDate, reset: true, confirm: true }); }
        else throw e;
      }
      toast.success('Volvió al monto calculado'); load();
    } catch (e: any) { toast.error(e?.message || 'No se pudo'); }
  }
  async function quitarExcepcion(s: PayrollSession) {
    if (!s.exception_id) return;
    try { await deletePayrollException(s.exception_id); toast.success('La sesión vuelve a pagarse al titular'); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
  }

  const totalMissing = resp ? resp.teachers.reduce((a, t) => a + t.missing_lists, 0) : 0;
  const month = effDate.slice(0, 7);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-line bg-surface p-0.5">
            {([['day', 'Día'], ['week', 'Semana'], ['month', 'Mes']] as const).map(([k, label]) => (
              <button key={k} onClick={() => setRange(k)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition ${range === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>{label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => nav(-1)} className="rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content-2 hover:bg-surface-2">←</button>
            <span className="min-w-[9rem] text-center text-sm font-medium capitalize text-content">{periodLabel(resp, range)}</span>
            <button onClick={() => nav(1)} className="rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content-2 hover:bg-surface-2">→</button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setExtraFor('any')} data-testid="extra-add" className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 hover:bg-surface-2"><Gift size={15} /> Bono / comisión</button>
          <button onClick={() => setShowRates(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 hover:bg-surface-2"><Settings2 size={15} /> Tarifas y sueldos</button>
        </div>
      </div>

      {loading || !resp ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : resp.teachers.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface"><EmptyState icon={Users} title="Sin clases en el periodo" subtitle="Ninguna clase con maestro programada en este rango." /></div>
      ) : (
        <>
          {/* Semana abierta (Mabel 9-oct-2026): no avanza por calendario, se cierra a mano */}
          {pw && (pw.is_open ? (
            <div data-testid="nomina-semana-abierta" className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${pw.pending.length ? 'border-amber-500/30 bg-amber-500/10' : 'border-emerald-500/30 bg-emerald-500/10'}`}>
              <div className="text-sm">
                <div className="font-semibold text-content">Semana por pagar</div>
                <div className="text-xs text-content-2" data-testid="nomina-pendientes">
                  {pw.pending.length ? `Faltan ${pw.pending.length}: ${pw.pending.map((p) => p.name.split(' ')[0]).join(', ')}` : 'Ya están todos pagados ✓'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {pw.can_reopen && (
                  <button onClick={reabrirSemana} className="inline-flex items-center gap-1 text-xs text-content-3 underline-offset-2 hover:text-content hover:underline"><RotateCcw size={12} /> Deshacer cierre</button>
                )}
                <button onClick={cerrarSemana} disabled={closing} data-testid="nomina-cerrar-semana"
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60 ${pw.pending.length ? 'border border-line bg-surface text-content-2 hover:bg-surface-2' : 'bg-emerald-600 text-white hover:opacity-90'}`}>
                  {closing ? '…' : 'Cerrar semana y pasar a la siguiente'}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAnchor('')} data-testid="nomina-ir-semana-abierta"
              className="mb-4 flex w-full items-center gap-2 rounded-2xl border border-line bg-surface-2/60 px-4 py-3 text-left text-xs text-content-2 hover:bg-surface-2">
              <AlertTriangle size={14} className="shrink-0 text-amber-500" /> Estás viendo otra semana. La semana por pagar es la del {fromISO(pw.open_week).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}. Toca aquí para ir a ella.
            </button>
          ))}

          {/* Total de la nómina */}
          <div className="mb-4 rounded-2xl border border-line bg-surface p-5 shadow-sm">
            <div className="text-sm text-content-2">Nómina · <span className="capitalize">{periodLabel(resp, range)}</span></div>
            <div className="font-display text-3xl font-bold text-red-600 dark:text-red-400" data-testid="nomina-total-periodo">{money(resp.totals.total ?? resp.totals.pay)}</div>
            <div className="text-xs text-content-3">{resp.totals.teachers} persona(s) · {resp.totals.hours} h · {resp.totals.sessions} sesiones programadas{resp.totals.extras > 0 && <span data-testid="nomina-extras-total"> · sueldos y clases {money(resp.totals.pay)} + bonos/comisiones {money(resp.totals.extras)}</span>}</div>
            <p className="mt-1 text-[11px] text-content-3">Se paga cada clase del horario aunque no se haya pasado lista. Si una clase no se dio o la dio un suplente, márcalo en la sesión.</p>
            {resp.adjustments_enabled && (
              <p className="mt-1 text-[11px] text-content-3" data-testid="nomina-ajustes-ayuda">
                ¿Hay que pagarle distinto a alguien esta semana? Toca <b>Editar monto</b> (debajo del monto) antes de «Registrar pago» (o usa <b>Ajustes</b> para bonos y horas extra) — no cambies la tarifa.
                {(resp.totals.adjustments ?? 0) !== 0 && <> Ajustes de la semana: <b>{(resp.totals.adjustments ?? 0) > 0 ? '+' : '−'}{money(Math.abs(resp.totals.adjustments ?? 0))}</b>.</>}
              </p>
            )}
            {resp.totals.missing_rates > 0 && (
              <button onClick={() => setShowRates(true)} className="mt-3 flex w-full items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-left text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-500/15">
                <AlertTriangle size={14} /> {resp.totals.missing_rates} maestro(s) sin tarifa — su sueldo no se está contando. Tócalo para fijarlas.
              </button>
            )}
            {totalMissing > 0 && (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-line bg-surface-2/60 px-3 py-2 text-xs text-content-2">
                <ClipboardX size={14} className="shrink-0 text-content-3" /> {totalMissing} sesión(es) sin lista tomada — se pagan igual; recepción debe pasar la lista.
              </div>
            )}
          </div>

          {/* Por maestro */}
          <div className="space-y-2">
            {resp.teachers.map((t) => (
              <div key={t.teacher_id} className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
                  <button onClick={() => setExpanded((e) => e === t.teacher_id ? null : t.teacher_id)}
                    className="flex min-w-0 flex-1 basis-[11rem] items-center gap-3 text-left">
                    {t.has_photo
                      ? <img src={teacherPhotoUrl(t.teacher_id)} alt="" className="h-9 w-9 rounded-full object-cover" />
                      : <div className="grid h-9 w-9 place-items-center rounded-full bg-surface-2 text-sm font-medium text-content-3">{t.teacher_name?.[0]}</div>}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-content">{t.teacher_name}</div>
                      <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-content-3">
                        {t.pay_mode === 'commission' ? (
                          <><Percent size={11} /> {t.commission_pct}% · {t.commission?.partial ? 'cobrado en el periodo' : <span className="capitalize">{monthLabel(t.commission?.month || month)}</span>} · base {money(t.commission?.base || 0)}
                            {t.commission && t.commission.pending > 0 && <span className="text-amber-600 dark:text-amber-400">· pendiente {money(t.commission.pending)}</span>}
                            {t.commission?.partial && <span className="text-content-3">· mes {money(t.commission.month_commission)}</span>}</>
                        ) : t.solo_extras ? (
                          <span>Solo bonos / comisiones en este periodo</span>
                        ) : t.has_salary && t.sessions.length === 0 ? (
                          <span data-testid={`salary-row-${t.teacher_id}`}>Sueldo fijo {money(t.salary_weekly || 0)}/semana{(t.weeks || 1) > 1 ? ` · ${t.weeks} semanas` : ''}</span>
                        ) : (
                          <><Clock size={11} /> {t.total_hours} h · {t.sessions.length} sesiones
                            {t.rate != null && t.rate > 0 ? (t.morning_rate != null ? ` · mañana ${money(t.morning_rate)} / tarde ${money(t.rate!)}` : ` · ${money(t.rate!)}/h`) : t.has_salary ? <span data-testid={`clases-incluidas-${t.teacher_id}`}> · clases incluidas en su sueldo</span> : ''}
                            {t.has_salary && <span data-testid={`salary-row-${t.teacher_id}`}> · + sueldo fijo {money(t.salary)}</span>}
                            {t.missing_lists > 0 && <span className="text-content-3">· {t.missing_lists} sin lista</span>}
                            {t.not_given > 0 && <span className="text-content-3">· {t.not_given} no dada(s)</span>}
                            {t.substituted > 0 && <span className="text-content-3">· {t.substituted} con suplente</span>}</>
                        )}
                      </div>
                    </div>
                    {expanded === t.teacher_id ? <ChevronDown size={16} className="shrink-0 text-content-3" /> : <ChevronRight size={16} className="shrink-0 text-content-3" />}
                  </button>
                  {(() => {
                    // «A pagar» (fuera del botón de expandir para poder llevar su lápiz). Monto editado (Mabel 19-oct-2026):
                    // solo vista Semana, sin pagar, quien se paga con «Registrar pago» (no comisión).
                    const directo = (t.adjustments || []).find((a) => a.concept === MONTO_DIRECTO) || null;
                    const editable = range === 'week' && resp.adjustments_enabled && t.has_rate && t.pay_mode !== 'commission' && !t.solo_extras;
                    const otrosAj = (t.adjustments_total || 0) - (directo ? directo.amount : 0);
                    const tip = directo ? [`Editado por ${directo.created_by_name || '—'}${directo.created_at ? ` el ${directo.created_at}` : ''}`, directo.note].filter(Boolean).join(' · ') : undefined;
                    return (
                      <span className="flex shrink-0 flex-col items-end" data-testid={`apagar-${t.teacher_id}`}>
                        <span className="flex items-center gap-1">
                          {t.has_rate
                            ? <span className="font-display text-lg font-bold text-content" data-testid={`pago-ajustado-${t.teacher_id}`} title={tip}>{money(t.pay_adjusted ?? t.pay!)}</span>
                            : t.solo_extras ? null
                            : <span className="rounded-full bg-amber-500/15 px-2 py-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">sin tarifa</span>}
                        </span>
                        {/* «Editar monto» con texto, debajo del monto: no confundir con el botón «Tarifa» (Mabel 19-oct-2026). */}
                        {editable && !t.paid && (
                          <button onClick={() => setMontoFor(t)} title="Editar el monto a pagar esta semana" aria-label={`Editar monto a pagar de ${t.teacher_name}`} data-testid={`monto-edit-${t.teacher_id}`}
                            className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[11px] font-semibold text-accent-ink hover:bg-surface-2 hover:underline"><Pencil size={11} /> Editar monto</button>
                        )}
                        {t.has_rate && directo
                          ? <span className="flex items-center gap-1 text-[11px] text-content-3" title={tip} data-testid={`monto-editado-${t.teacher_id}`}>
                              <span className="rounded bg-amber-500/15 px-1 font-semibold text-amber-700 dark:text-amber-400">editado</span>
                              calculado {money(Math.round(((t.pay_adjusted ?? 0) - directo.amount) * 100) / 100)}
                              {editable && !t.paid && <button onClick={() => volverAlCalculado(t)} title="Volver al calculado" aria-label="Volver al calculado" data-testid={`monto-reset-${t.teacher_id}`} className="rounded p-0.5 hover:bg-surface-2 hover:text-content"><RotateCcw size={11} /></button>}
                            </span>
                          : t.has_rate && otrosAj
                          ? <span className="text-[11px] text-content-3">calculado {money(t.pay!)} {otrosAj > 0 ? '+' : '−'} {money(Math.abs(otrosAj))} ajustes</span>
                          : null}
                        {t.extras_total > 0 && <span className="text-xs font-semibold text-content-2" data-testid={`extras-total-${t.teacher_id}`}>{t.has_rate ? '+ ' : ''}{money(t.extras_total)} bonos</span>}
                        {editable && t.paid && <span className="max-w-[9.5rem] text-right text-[10px] leading-tight text-content-3" data-testid={`monto-pagada-${t.teacher_id}`}>Semana pagada: para corregir, deshaz el pago</span>}
                      </span>
                    );
                  })()}
                  {/* Acciones: en el celular bajan a su propia línea (flex-wrap) para no aplastar nombre y monto. */}
                  <div className="ml-auto flex shrink-0 items-center gap-2">
                  <button onClick={() => setShowRates(t.teacher_id)} title="Cambiar el sueldo o la tarifa (no el monto de esta semana)" aria-label={`Tarifa de ${t.teacher_name}`} data-testid={`rate-edit-${t.teacher_id}`}
                    className="shrink-0 rounded-lg border border-line bg-surface px-2 py-1.5 text-xs font-medium text-content-2 hover:bg-surface-2 hover:text-content">Tarifa</button>
                  <button onClick={() => setExtraFor({ id: t.teacher_id, name: t.teacher_name })} title="Agregar bono, comisión u otro" data-testid={`extra-add-${t.teacher_id}`}
                    className="shrink-0 rounded-lg border border-line bg-surface p-1.5 text-content-3 hover:bg-surface-2 hover:text-content"><Plus size={14} /></button>
                  {resp.adjustments_enabled && t.has_rate && t.pay_mode !== 'commission' && !t.solo_extras && (
                    <button onClick={() => setAjusteFor(t)} title="Ajuste de esta semana: falta, permiso, retardo, horas extra, vacaciones, bonos" data-testid={`ajuste-add-${t.teacher_id}`}
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-line bg-surface px-2 py-1.5 text-xs font-medium text-content-2 hover:bg-surface-2 hover:text-content">
                      <SlidersHorizontal size={14} /><span className="hidden sm:inline">Ajustes</span>
                    </button>
                  )}
                  {t.has_rate && t.pay_mode !== 'commission' && (
                    t.paid
                      ? <button onClick={() => deshacer(t)} disabled={busy === t.teacher_id}
                          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-emerald-500/10 px-2.5 py-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-60" title="Pagado · clic para deshacer">
                          <CheckCircle2 size={14} /> Pagado <RotateCcw size={12} className="opacity-60" />
                        </button>
                      : <button onClick={() => pagar(t)} disabled={busy === t.teacher_id}
                          className="shrink-0 rounded-lg bg-accent px-2.5 py-1.5 text-xs font-semibold text-on-accent hover:opacity-90 disabled:opacity-60">
                          {busy === t.teacher_id ? '…' : 'Registrar pago'}
                        </button>
                  )}
                  </div>
                </div>
                {(t.adjustments || []).some((a) => a.extra_id == null && a.concept !== MONTO_DIRECTO) && (
                  <div className="flex flex-wrap items-center gap-1.5 border-t border-line/60 px-4 py-2" data-testid={`ajustes-${t.teacher_id}`}>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-content-3">Ajustes de la semana</span>
                    {t.adjustments!.filter((a) => a.extra_id == null && a.concept !== MONTO_DIRECTO).map((a) => (
                      <span key={a.id} data-testid={`ajuste-${a.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${a.amount < 0 ? 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'}`}
                        title={[a.note, a.created_by_name ? `capturó ${a.created_by_name}` : '', a.estado === 'incluido' ? 'ya venía incluido en el pago registrado' : a.estado === 'pagado' ? 'ya pagado' : 'se suma al registrar el pago'].filter(Boolean).join(' · ')}>
                        {a.nombre} <b>{a.amount < 0 ? '−' : '+'}{money(Math.abs(a.amount))}</b>
                        {a.estado === 'incluido' && <span className="text-[10px] opacity-80">ya incluido</span>}
                        {a.estado !== 'pagado' && <button onClick={() => quitarAjuste(t, a)} title="Quitar ajuste" data-testid={`ajuste-del-${a.id}`} className="rounded p-0.5 opacity-70 hover:bg-black/5 hover:opacity-100"><X size={11} /></button>}
                      </span>
                    ))}
                  </div>
                )}
                {t.extras.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 border-t border-line/60 px-4 py-2" data-testid={`extras-${t.teacher_id}`}>
                    {t.extras.map((x) => {
                      const nombre = x.concept === 'otro' ? (x.label || 'Otro') : (x.label ? `${x.concept_label} · ${x.label}` : x.concept_label);
                      return (
                        <span key={x.id} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2/70 px-2.5 py-1 text-xs text-content-2" title={[x.note, x.created_by_name ? `registró ${x.created_by_name}` : ''].filter(Boolean).join(' · ')}>
                          <Gift size={11} className="text-content-3" /> {nombre} <b className="text-content">{money(x.amount)}</b>
                          {x.from_adjustment
                            ? <span className="text-[10px] text-content-3" title="Salió de un ajuste de la semana: se quita deshaciendo «Pagado»">ajuste</span>
                            : <button onClick={() => quitarExtra(t, x.id, nombre)} title="Quitar (borra el gasto)" data-testid={`extra-del-${x.id}`} className="rounded p-0.5 text-content-3 hover:bg-red-500/10 hover:text-red-600"><X size={11} /></button>}
                        </span>
                      );
                    })}
                  </div>
                )}
                {expanded === t.teacher_id && !t.solo_extras && (
                  t.pay_mode === 'commission'
                    ? <CommissionSheet teacherId={t.teacher_id} month={t.commission?.month || month} onChanged={load} />
                    : (
                      <div className="border-t border-line bg-surface-2/40 px-4 py-2">
                        {t.sessions.map((s, i) => (
                          <div key={i} className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5 text-sm ${s.kind ? 'opacity-70' : ''}`}>
                            <span className="min-w-0 flex-1 capitalize text-content-2">
                              {fmtDay(s.date)}{s.start_time ? ` ${s.start_time}` : ''} · {s.class_name}
                              {!s.scheduled && <span className="ml-1 rounded bg-surface-2 px-1 text-[10px] normal-case text-content-3">fuera de horario</span>}
                              {s.kind === 'not_given' && <span className="ml-1 rounded bg-red-500/10 px-1 text-[10px] normal-case text-red-600 dark:text-red-400">no se dio</span>}
                              {s.kind === 'substitute' && s.substitute_name && <span className="ml-1 rounded bg-sky-500/10 px-1 text-[10px] normal-case text-sky-600 dark:text-sky-400">la dio {s.substitute_name}</span>}
                              {s.substitute_for && <span className="ml-1 rounded bg-sky-500/10 px-1 text-[10px] normal-case text-sky-600 dark:text-sky-400">suplió a {s.substitute_for}</span>}
                              {s.kind && s.marked_by && <span className="ml-1 text-[10px] normal-case text-content-3">· marcó {s.marked_by}</span>}
                              {!s.has_list && !s.kind && <span className="ml-1 rounded bg-amber-500/10 px-1 text-[10px] normal-case text-amber-600 dark:text-amber-400">sin lista</span>}
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="tabular-nums text-content-3">{s.hours} h{s.rate != null && s.hours > 0 ? ` · ${money(s.pay)}` : ''}</span>
                              {s.kind && !s.substitute_for
                                ? <button onClick={() => quitarExcepcion(s)} title="Quitar y volver a pagar al titular" className="rounded p-1 text-content-3 hover:bg-surface-2 hover:text-content"><RotateCcw size={13} /></button>
                                : !s.substitute_for && (
                                  <>
                                    <button onClick={() => noSeDio(s)} title="No se dio: no se paga" className="rounded p-1 text-content-3 hover:bg-red-500/10 hover:text-red-600"><ClipboardX size={13} /></button>
                                    <button onClick={() => setSubFor(s)} title="La dio un suplente" className="rounded p-1 text-content-3 hover:bg-sky-500/10 hover:text-sky-600"><UserCheck size={13} /></button>
                                  </>
                                )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {showRates !== false && <RatesModal focusId={typeof showRates === 'number' ? showRates : undefined} onClose={() => setShowRates(false)} onSaved={load} />}
      {subFor && <SubstituteModal session={subFor} teachers={teachers} onClose={() => setSubFor(null)} onSaved={load} />}
      {montoFor && <MontoModal t={montoFor} anchor={effDate} periodText={periodLabel(resp, range)} onClose={() => setMontoFor(null)} onSaved={load} />}
      {ajusteFor && <AjusteModal t={ajusteFor} anchor={effDate} periodText={periodLabel(resp, range)} onClose={() => setAjusteFor(null)} onSaved={load} />}
      {extraFor && <ExtraModal person={extraFor === 'any' ? null : extraFor} people={teachers} range={range} anchor={effDate} periodText={periodLabel(resp, range)} onClose={() => setExtraFor(null)} onSaved={load} />}
    </div>
  );
}
